import React, { useEffect, useState } from "react";
import { useLanguage } from "../../../Authentication/LanguageContext";
import ServerUrl from "../../../../BACKEND";
import "./A1Conversations.css";

interface ConversationItem {
  id: number;
  headergerman: string;
  headertamil: string;
  headerenglish: string;
  headersinhala: string;
  headerhindi: string;
  headerarabic: string;
  headerturkish: string;
  headerrussian: string;
  headerchinese: string;
  headerfrench: string;
  headerspanish: string;
  subheadergerman: string;
  subheadertamil: string;
  subheaderenglish: string;
  subheadersinhala: string;
  subheaderhindi: string;
  subheaderarabic: string;
  subheaderturkish: string;
  subheaderrussian: string;
  subheaderchinese: string;
  subheaderfrench: string;
  subheaderspanish: string;
  person1image: string;
  person2image: string;
  [key: string]: string | number;
}

const capitalize = (text: string) => {
  return text ? text.charAt(0).toUpperCase() + text.slice(1) : "";
};

const A1Conversations: React.FC = () => {
  const [conversations, setConversations] = useState<ConversationItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentlyPlaying, setCurrentlyPlaying] = useState<string | null>(null);
  const { language } = useLanguage();

  const translations = {
    title: {
      en: "Conversations",
      de: "Gespräche",
      ta: "உரையாடல்கள்",
      si: "කථා බහ",
      hi: "बातचीत",
      ar: "المحادثات",
      tr: "Konuşmalar",
      ru: "Разговоры",
      zh: "对话",
      fr: "Conversations",
      es: "Conversaciones",
    },
    subtitle: {
      en: "Practice German Conversations",
      de: "Deutsche Gespräche üben",
      ta: "ஜெர்மன் உரையாடல்களை பயிற்சி செய்யுங்கள்",
      si: "ජර්මානු සංවාද පුහුණු කරන්න",
      hi: "जर्मन वार्तालाप का अभ्यास करें",
      ar: "تدرب على المحادثات الألمانية",
      tr: "Almanca konuşmalar pratik yapın",
      ru: "Практика немецких разговоров",
      zh: "练习德语对话",
      fr: "Pratiquer des conversations en allemand",
      es: "Practica conversaciones en alemán",
    },
  };

  const languageMap: Record<string, string> = {
    en: "english",
    de: "german",
    ta: "tamil",
    si: "sinhala",
    hi: "hindi",
    ar: "arabic",
    tr: "turkish",
    ru: "russian",
    zh: "chinese",
    fr: "french",
    es: "spanish",
  };

  useEffect(() => {
    fetch(`${ServerUrl}/api/conversations`)
      .then((res) => res.json())
      .then((json) => setConversations(json))
      .catch((error) => console.error("Error loading conversations:", error))
      .finally(() => setLoading(false));
  }, [language]);

  const speakText = (text: string) => {
    if (currentlyPlaying === text) {
      setCurrentlyPlaying(null);
      speechSynthesis.cancel();
      return;
    }

    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = "de-DE";
    speechSynthesis.speak(utterance);
    setCurrentlyPlaying(text);

    utterance.onend = () => {
      setCurrentlyPlaying(null);
    };
  };

  const getTranslation = (item: ConversationItem, fieldPrefix: string) => {
    const fieldName = `${fieldPrefix}${languageMap[language] || "german"}`;
    const value = item[fieldName];
    return typeof value === "string" ? capitalize(value) : "";
  };

  return (
    <div className="conversations-page-container">
      <div className="conversations-page-header">
        <h1 className="conversations-page-title">{translations.title[language]}</h1>
        <p className="conversations-page-subtitle">{translations.subtitle[language]}</p>
      </div>

      {loading ? (
        <div className="conversations-page-loading">Loading...</div>
      ) : (
        <div className="conversations-page-scroll-container">
          {conversations.map((conv, index) => (
            <div key={conv.id} className="conversations-page-block-card">
              <div className="conversations-page-block-header-container">
                <h2 className="conversations-page-block-header">
                  {`${index + 1}. ${conv.headergerman}`}
                </h2>
                {getTranslation(conv, "header") && (
                  <p className="conversations-page-block-header-translated">
                    {getTranslation(conv, "header")}
                  </p>
                )}
              </div>

              <div className="conversations-page-subheader-container">
                <p className="conversations-page-block-subheader">{conv.subheadergerman}</p>
                {getTranslation(conv, "subheader") && (
                  <p className="conversations-page-block-subheader-translated">
                    {getTranslation(conv, "subheader")}
                  </p>
                )}
              </div>

              <div className="conversations-page-dialog-container">
                {Array.from({ length: 10 }).map((_, i) => {
                  const dialogNumber = i + 1;
                  const german = conv[`dialog${dialogNumber}german`];
                  const translated = conv[`dialog${dialogNumber}${languageMap[language]}`];

                  if (!german) return null;

                  const image =
                    i % 2 === 0 ? `${ServerUrl}/storage/${conv.person1image}` : `${ServerUrl}/storage/${conv.person2image}`;
                  const isPlaying = currentlyPlaying === german;

                  return (
                    <div
                      key={`dialog-${conv.id}-${dialogNumber}`}
                      className={`conversations-page-dialog-row ${
                        isPlaying ? "conversations-page-playing" : ""
                      }`}
                      onClick={() => speakText(german as string)}
                    >
                      {image && (
                        <img
                          src={image}
                          alt="Speaker"
                          className="conversations-page-small-image"
                          onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            target.src = `${ServerUrl}/A1Image/placeholder.png`;
                          }}
                        />
                      )}
                      <div className="conversations-page-dialog-content">
                        <p className="conversations-page-dialog-text">{german}</p>
                        {translated && (
                          <p className="conversations-page-dialog-translation">{translated}</p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default A1Conversations;